import { fetchArticle } from '@elevio/kb-kit/lib/api/article';
import { Categories } from '@elevio/kb-kit/lib/components';
import * as Search from '@elevio/kb-kit/lib/components/search';
import { useCategories } from '@elevio/kb-kit/lib/hooks';
import { useEffect, useRef, useState } from 'react';
import CategoryCard from '../components/CategoryCard';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { MainSection } from '../components/layouts/Main';
import PageLayout from '../components/layouts/Page';
import { getSubmissions } from '../requests/user-submission';
import { fetchAgencyFlags } from '../requests/agency-flags';
import { DEFAULT_CRM_TOKEN, homeArticleIds } from '../utils/constants';
import { privateSubmissionUrl } from '../utils/privateSubmissionUrl';
import Validator from '../utils/Validators';
const isIOS = () => {
    return navigator.userAgent.match(/iPhone/i) != null;
};
const validator = new Validator();
const AGENCY_ID = 75;
const openPrivateSubmission = (path) => {
    const url = privateSubmissionUrl(path);
    if (!url) {
        return;
    }
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
        newWindow.opener = null;
    }
};
export const getHeader = () => {
    return (<Header hideSearch>
      <div className="home-hero-section">
        <h1 className="home-hero-title">Coyote Management Portal</h1>
        <Search.Input placeholder="Search portal..." data-testid="search-input" className="hero-search-center"/>
      </div>
    </Header>);
};
const HomePage = () => {
    const { categories } = useCategories();
    const [displayRequestField, setDisplayRequestField] = useState(false);
    const [contactInfo, setContactInfo] = useState('');
    const [displayRequestFieldType, setDisplayRequestFieldType] = useState('EMAIL');
    const [errorMessage, setErrorMessage] = useState('');
    const [responseMessage, setResponseMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [showViewAllRequests, setShowViewAllRequests] = useState(false);
    const [articles, setArticles] = useState([]);
    const requestSubmissionContainer = useRef(document.createElement('div'));
    const requestSubmissionContainerMobile = useRef(document.createElement('div'));
    useEffect(() => {
        document.addEventListener('mousedown', handleClick);
        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    }, []);
    useEffect(() => {
        let cancelled = false;
        fetchAgencyFlags(AGENCY_ID).then((data) => {
            if (!cancelled) {
                setShowViewAllRequests(Boolean(data === null || data === void 0 ? void 0 : data.isPublicMapEnabled));
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);
    useEffect(() => {
        const fetchArticles = async () => {
            try {
                const responses = await Promise.all(homeArticleIds.map((id) => fetchArticle(id, '1')));
                setArticles(responses);
            }
            catch (error) {
                console.error('Error fetching articles:', error);
            }
        };
        fetchArticles();
    }, []);
    const isMobile = () => {
        return (navigator.userAgent.match(/Android/i) != null ||
            navigator.userAgent.match(/iPhone/i) != null);
    };
    const handleClick = (e) => {
        if (isMobile() &&
            requestSubmissionContainerMobile.current &&
            !requestSubmissionContainerMobile.current.contains(e.target)) {
            setErrorMessage('');
            setDisplayRequestField(false);
            setResponseMessage('');
        }
        else if (!isMobile() &&
            requestSubmissionContainer.current &&
            !requestSubmissionContainer.current.contains(e.target)) {
            setErrorMessage('');
            setDisplayRequestField(false);
            setResponseMessage('');
        }
    };
    const toggleRequestField = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (displayRequestField) {
            setErrorMessage('');
        }
        setDisplayRequestField(!displayRequestField);
    };
    const getRequestFieldTypeHandler = (type) => {
        return () => {
            setDisplayRequestFieldType(type);
            setErrorMessage('');
        };
    };
    const isEmailSelected = displayRequestFieldType === 'EMAIL';
    const isPhoneNumberSelected = displayRequestFieldType === 'PHONE_NUMBER';
    const onRequestSubmissionClick = () => {
        setErrorMessage('');
        if (contactInfo &&
            (validator.isEmail(contactInfo) ||
                validator.isPhone(contactInfo))) {
            const token = process.env.CRM_TOKEN || DEFAULT_CRM_TOKEN;
            setLoading(true);
            getSubmissions(token, contactInfo)
                .then((result) => {
                setContactInfo('');
                setResponseMessage(result.message);
                setDisplayRequestField(false);
            })
                .catch((error) => {
                var _a, _b;
                setErrorMessage((_b = (_a = error === null || error === void 0 ? void 0 : error.message) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : 'Failed loading submissions');
            })
                .finally(() => setLoading(false));
        }
        else {
            let errorMessage = `Incorrect ${isEmailSelected ? 'email' : 'mobile number'} format`;
            if (!contactInfo || (contactInfo && !contactInfo.trim())) {
                errorMessage = 'The email or mobile number is required.';
            }
            setErrorMessage(errorMessage);
        }
    };
    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            onRequestSubmissionClick();
        }
    };
    const handleChangeEmail = (event) => {
        var _a;
        const value = (_a = event === null || event === void 0 ? void 0 : event.target) === null || _a === void 0 ? void 0 : _a.value;
        setContactInfo(value);
    };
    const handleSubmitRequest = (e) => {
        e.preventDefault();
        e.stopPropagation();
        openPrivateSubmission('create');
    };
    const handleViewMyRequests = (e) => {
        e.preventDefault();
        e.stopPropagation();
        openPrivateSubmission('listing');
    };
    const handleViewAllRequests = (e) => {
        e.preventDefault();
        e.stopPropagation();
        openPrivateSubmission('public-map');
    };
    const submitRequestUrl = privateSubmissionUrl('create');
    return (<PageLayout header={getHeader()} footer={<Footer />}>
      <MainSection className="page-text-color">
        <div className="login-button-container">
          <section className="button-container button-container--three">
            <div className="button-wrapper">
              <button type="button" className="cta-request" onClick={handleSubmitRequest}>
                <img src={`${window.assetsLocation}/images/request.svg`} alt="request icon" aria-hidden/>
                Submit a Request
              </button>
            </div>
            <div className="button-wrapper">
              <button type="button" className="cta-request" onClick={handleViewMyRequests}>
                <img src={`${window.assetsLocation}/images/inbox.svg`} alt="inbox icon" aria-hidden/>
                View My Requests
              </button>
            </div>
            {showViewAllRequests ? (<div className="button-wrapper">
                <button type="button" className="cta-request" onClick={handleViewAllRequests}>
                  <img src={`${window.assetsLocation}/images/map.svg`} alt="map icon" aria-hidden/>
                  View All Requests
                </button>
              </div>) : null}
          </section>
        </div>
        <section className="home-categories-grid">
          <a href={submitRequestUrl} target="_blank" rel="noopener noreferrer" className="card category-card submission-card">
            <div className="card-icon-circle">!</div>
            <h2 className="card-title">Report Issue / Ask Question</h2>
            <span className="card-link">Click here to start a submission</span>
          </a>
          <Categories.Loop>
            <CategoryCard />
          </Categories.Loop>
        </section>
      </MainSection>
    </PageLayout>);
};
export default HomePage;
